/**
 * The stacked (phone) split's two floors, fitted to the height the split actually has.
 *
 * The floors that suit a tall phone are HEIGHTS, measured at 390×844 (a 727px split under the page
 * header): the runs band needs 15rem (240px; ~234px measured) for its toolbar (56px, wrapped), the table
 * header (49px) and one whole run row (91px) with the stacked padding, and the chat 29rem for its header, chips and
 * composer (~201px) and a 260px conversation. Together they are 44rem, and many phones are shorter
 * than that: 375×667 leaves the split ~471px and 360×640 ~442px. When the floors cannot both hold,
 * react-resizable-panels gives up on them and pins the handle (`aria-valuemin` = `valuemax`), so a
 * drag or an arrow key does nothing.
 *
 * So the floors are derived from the measured height, and the chat's gives way first:
 *  - Room for both, plus `MIN_TRAVEL` for the handle: the 15rem / 29rem floors, unchanged.
 *  - Otherwise the runs keep 15rem (one whole row), capped at `RUNS_MAX_SHARE` of the split so a
 *    very short window still leaves the chat a share, and the chat's floor drops to half of what is
 *    left, so the handle always has the other half to travel.
 *
 * When they fit, the rem floors pass through as they are, so a tall phone lays out exactly as before.
 * When they do not, the floors are percentages of the split, the unit that follows its height.
 * `height` 0 is "not measured yet" (the first paint, jsdom): the rem floors pass through then too.
 */
const RUNS_MIN_REM = 15;
const CHAT_MIN_REM = 29;
/** The least handle travel that still counts as "the floors fit", in rem. */
const MIN_TRAVEL_REM = 1;
/** The most of a short split the runs floor may claim. */
const RUNS_MAX_SHARE = 0.6;

export interface StackedSplitFloors {
  runsMin: string;
  chatMin: string;
}

function percent(part: number, whole: number): string {
  // Floor to 0.01%: rounding up could push the two floors' sum past what the split holds.
  return `${Math.floor((part / whole) * 10000) / 100}%`;
}

export function stackedSplitFloors(height: number, remPx = 16): StackedSplitFloors {
  const runs = RUNS_MIN_REM * remPx;
  const chat = CHAT_MIN_REM * remPx;
  if (!(height > 0) || runs + chat + MIN_TRAVEL_REM * remPx <= height) {
    return { runsMin: `${RUNS_MIN_REM}rem`, chatMin: `${CHAT_MIN_REM}rem` };
  }
  const runsFloor = Math.min(runs, height * RUNS_MAX_SHARE);
  const chatFloor = (height - runsFloor) / 2;
  return { runsMin: percent(runsFloor, height), chatMin: percent(chatFloor, height) };
}
